import { useEffect, useState } from 'react';
import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { Check, Fingerprint, KeyRound, LogIn } from 'lucide-react';
import type { SessionEndReason } from '@/lib/auth';
import type {
  CurrentUserResponse,
  ItemResponse,
  LoginResponse,
  PasskeyLoginOptionsResponse,
  PasskeyRegisterOptionsResponse,
  PublicBrandingResponse,
  SessionResponse,
  TotpSetupResponse,
} from '@proerp/shared';
import {
  Button,
  FormField,
  Input,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  Label,
  Spinner,
} from '@proerp/ui';
import { ApiError, apiRequest, describeError } from '@/lib/apiClient';
import { guessDeviceLabel, initAuth, storeToken, takeSessionEndReason } from '@/lib/auth';
import { useSessionWatch } from '@/lib/useSessionWatch';
import { readCachedBranding, writeCachedBranding } from '@/lib/brandingCache';
import { QrCanvas } from '@/components/QrCanvas';

type GateState = 'loading' | 'open' | 'login' | 'unreachable';
/**
 * Zweiter Faktor ist TOTP ODER Passkey (ADR 0034), gleichrangig — beim
 * allerersten Login wählt der User frei ('setup-choice'); ist beides bereits
 * eingerichtet, wählt er je Login ('second-factor-choice').
 */
type LoginStep =
  | 'credentials'
  | 'setup-choice'
  | 'totp-setup'
  | 'totp-verify'
  | 'passkey-setup'
  | 'second-factor-choice'
  | 'passkey-verify';

/** Standard-Branding (Fallback, wenn in den Einstellungen nichts hinterlegt ist). */
const DEFAULT_BRANDING: PublicBrandingResponse = {
  companyName: 'PROBIG',
  loginTitle: 'Das ERP für den Anlagenbau.',
  loginSubtitle:
    // Nur aufzählen, was es gibt — Produktion kommt dazu, wenn das Modul live ist
    // (Entscheidung Tech-Lead, Qualitäts-Audit 2026-09-28).
    'Projekte, Angebote, Einkauf, Lager, Montage und HR — auf einer durchgängigen Datenbasis, mit dem Projekt als zentraler Klammer.',
  loginBullets: [
    'Projekt als zentrale Klammer für alle Buchungen',
    'Vom Angebot bis zur Schlussrechnung',
    'Lager, Beschaffung & Montage integriert',
  ],
  loginPanelColor: null,
  appLogoDataUrl: null,
  appLogoScalePercent: null,
};

/** Hinterlegtes Branding mit Defaults zusammenführen (leere Felder = Default). */
function mergeBranding(resp: PublicBrandingResponse): PublicBrandingResponse {
  return {
    companyName: resp.companyName || DEFAULT_BRANDING.companyName,
    loginTitle: resp.loginTitle || DEFAULT_BRANDING.loginTitle,
    loginSubtitle: resp.loginSubtitle || DEFAULT_BRANDING.loginSubtitle,
    loginBullets: resp.loginBullets.length ? resp.loginBullets : DEFAULT_BRANDING.loginBullets,
    loginPanelColor: resp.loginPanelColor || null,
    appLogoDataUrl: resp.appLogoDataUrl || null,
    appLogoScalePercent: resp.appLogoScalePercent ?? null,
  };
}

/**
 * Schützt die App mit dem eigenen Login (Passwort + TOTP-2FA, ADR 0012).
 * Im Dev-Modus (Backend ohne AUTH_JWT_SECRET) ist die Komponente ein Durchreicher.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GateState>('loading');
  /**
   * Grund des letzten Sitzungsendes, damit die Anmeldeseite sagen kann, warum
   * sie da ist. `takeSessionEndReason()` ist je Seitenaufbau idempotent — im
   * StrictMode läuft dieser Initialisierer doppelt.
   */
  const [endReason] = useState(takeSessionEndReason);
  // Sofort das zuletzt geladene Branding zeigen (falls vorhanden) statt des
  // Standard-Brandings, solange `/v1/branding` noch unterwegs ist — sonst
  // blitzt bei langsamer Verbindung/Server-Kaltstart kurz das Standard-
  // Branding auf, obwohl längst ein eigenes hinterlegt ist.
  const [branding, setBranding] = useState<PublicBrandingResponse>(() => readCachedBranding() ?? DEFAULT_BRANDING);

  // Verlängert die Sitzung bei Aktivität und meldet nach 12 h Ruhe ab (ADR 0106).
  // Hängt bewusst hier und nicht tiefer in der App: Der Gate ist die einzige
  // Stelle, die es in JEDEM angemeldeten Zustand gibt.
  useSessionWatch();

  const [unreachableReason, setUnreachableReason] = useState('');
  const [checkRun, setCheckRun] = useState(0);

  useEffect(() => {
    initAuth();
    let active = true;
    apiRequest<ItemResponse<CurrentUserResponse>>('/v1/auth/me')
      .then((response) => {
        if (!active) return;
        setState(response.data.isAuthenticated ? 'open' : 'login');
      })
      .catch((error: unknown) => {
        if (!active) return;
        // Nur „nicht angemeldet" führt zur Anmeldung. 429 (Rate-Limit), 5xx oder
        // ein Netzwerkausfall sagen nichts über die Sitzung — bis 2026-09-28 landeten
        // angemeldete Kollegen dadurch auf der Anmeldeseite.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          setState('login');
          return;
        }
        setUnreachableReason(describeError(error));
        setState('unreachable');
      });
    return () => {
      active = false;
    };
  }, [checkRun]);

  useEffect(() => {
    let active = true;
    // Branding der Anmeldeseite (öffentlich, kein Login nötig).
    apiRequest<ItemResponse<PublicBrandingResponse>>('/v1/branding')
      .then((response) => {
        if (!active) return;
        const merged = mergeBranding(response.data);
        setBranding(merged);
        writeCachedBranding(merged);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  if (state === 'open') {
    return <>{children}</>;
  }
  if (state === 'unreachable') {
    return (
      <Shell
        title="Server gerade nicht erreichbar"
        subtitle="Deine Anmeldung ließ sich nicht prüfen — das liegt nicht an dir."
        branding={branding}
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">{unreachableReason}</p>
          <Button
            variant="primary"
            onClick={() => {
              setState('loading');
              setCheckRun((run) => run + 1);
            }}
          >
            Erneut versuchen
          </Button>
        </div>
      </Shell>
    );
  }
  if (state === 'loading') {
    return (
      <Shell title="Einen Moment …" subtitle="Deine Anmeldung wird geprüft." branding={branding}>
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner aria-hidden />
          Bitte warten …
        </p>
      </Shell>
    );
  }
  return <LoginScreen branding={branding} endReason={endReason} onSuccess={() => window.location.reload()} />;
}

/**
 * Markenlogo. Ist ein App-Logo hinterlegt (`logoDataUrl`), wird es gezeigt — im
 * Markenpanel (`tone="invert"`) auf einer weißen Kachel, damit es auf dunklem Grund
 * lesbar bleibt. Sonst das Standard-Zeichen „P" + „ProERP".
 */
function BrandMark({
  tone = 'default',
  logoDataUrl = null,
}: {
  tone?: 'default' | 'invert';
  logoDataUrl?: string | null;
}) {
  if (logoDataUrl) {
    return (
      <span
        className={
          tone === 'invert'
            ? 'inline-flex items-center rounded-lg bg-white px-2.5 py-1.5'
            : 'inline-flex items-center'
        }
      >
        <img src={logoDataUrl} alt="Logo" className="h-8 max-w-48 object-contain" />
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2.5">
      <span
        className={
          tone === 'invert'
            ? 'flex size-9 items-center justify-center rounded-lg bg-white text-sm font-bold text-neutral-900'
            : 'flex size-9 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground'
        }
      >
        P
      </span>
      <span className={tone === 'invert' ? 'text-base font-semibold tracking-tight text-white' : 'text-base font-semibold tracking-tight text-foreground'}>
        ProERP
      </span>
    </span>
  );
}

/**
 * Anmelde-Rahmen: zweispaltig (Markenpanel links, Formular rechts) ab `lg`,
 * darunter nur das zentrierte Formular. Der Inhalt (Schritt/Lade-/Fehlerzustand)
 * kommt als `children`.
 */
function Shell({
  title,
  subtitle,
  branding,
  children,
}: {
  title: string;
  subtitle?: string;
  branding: PublicBrandingResponse;
  children: React.ReactNode;
}) {
  const year = new Date().getFullYear();
  // Hinterlegte Panel-Farbe als Hintergrund; sonst der dunkle Standard-Verlauf.
  const hasColor = Boolean(branding.loginPanelColor);
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Markenpanel — dunkel/ruhig, Texte & Farbe aus den Einstellungen. */}
      <aside
        className={
          hasColor
            ? 'relative hidden flex-col justify-between overflow-hidden p-10 text-white lg:flex'
            : 'relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-neutral-900 to-neutral-800 p-10 text-white lg:flex'
        }
        style={hasColor ? { backgroundColor: branding.loginPanelColor! } : undefined}
      >
        <div aria-hidden className="pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-white/5 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-16 size-80 rounded-full bg-white/[0.04] blur-3xl" />

        <BrandMark tone="invert" logoDataUrl={branding.appLogoDataUrl} />

        <div className="relative max-w-md">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">{branding.loginTitle}</h2>
          {branding.loginSubtitle && (
            <p className="mt-3 text-sm leading-relaxed text-white/70">{branding.loginSubtitle}</p>
          )}
          {branding.loginBullets.length > 0 && (
            <ul className="mt-7 flex flex-col gap-3 text-sm text-white/80">
              {branding.loginBullets.map((point) => (
                <li key={point} className="flex items-center gap-2.5">
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-white/10">
                    <Check className="size-3" strokeWidth={2.5} aria-hidden />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="relative text-xs text-white/45">
          © {year} {branding.companyName} · ProERP
        </p>
      </aside>

      {/* Formularseite */}
      <main className="flex items-center justify-center bg-muted/30 p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <BrandMark logoDataUrl={branding.appLogoDataUrl} />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
          {subtitle && <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>}
          <div className="mt-7">{children}</div>
        </div>
      </main>
    </div>
  );
}

/**
 * Warum die Anmeldeseite da ist. Zwei Gründe, zwei Sätze — um 3 Uhr „12 Stunden
 * ohne Aktivität" zu behaupten wäre schlicht falsch (ADR 0106/0107).
 */
const SESSION_END_TEXT: Record<SessionEndReason, string> = {
  idle: 'Deine Sitzung wurde nach 12 Stunden ohne Aktivität beendet. Bitte melde dich neu an.',
  daily: 'Sitzungen enden jede Nacht um 3 Uhr — deine wurde dabei beendet. Bitte melde dich neu an.',
};

const STEP_META: Record<LoginStep, { title: string; subtitle: string }> = {
  credentials: { title: 'Willkommen zurück', subtitle: 'Bitte melde dich mit deinen Zugangsdaten an.' },
  'setup-choice': {
    title: 'Zweiten Faktor einrichten',
    subtitle: 'Wähle, wie du dich künftig zusätzlich zum Passwort bestätigst.',
  },
  'totp-setup': { title: 'Authenticator-App einrichten', subtitle: 'Sichere dein Konto mit einer Authenticator-App.' },
  'totp-verify': { title: 'Zwei-Faktor-Bestätigung', subtitle: 'Gib den 6-stelligen Code aus deiner App ein.' },
  'passkey-setup': { title: 'Passkey einrichten', subtitle: 'Folge den Anweisungen deines Geräts (Face ID, Touch ID, PIN …).' },
  'second-factor-choice': {
    title: 'Zweiten Faktor bestätigen',
    subtitle: 'Wähle Authenticator-Code oder Passkey.',
  },
  'passkey-verify': { title: 'Mit Passkey anmelden', subtitle: 'Folge den Anweisungen deines Geräts (Face ID, Touch ID, PIN …).' },
};

function LoginScreen({
  branding,
  endReason,
  onSuccess,
}: {
  branding: PublicBrandingResponse;
  endReason: SessionEndReason | null;
  onSuccess: () => void;
}) {
  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [setup, setSetup] = useState<TotpSetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  /** Beide Faktoren eingerichtet → auf 'totp-verify'/'passkey-verify' den Rückweg zur Auswahl zeigen. */
  const [hasChoice, setHasChoice] = useState(false);

  async function submitCredentials(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setIsPending(true);
    try {
      const response = await apiRequest<ItemResponse<LoginResponse>>('/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), password }),
      });
      setPendingToken(response.data.pendingToken);
      setHasChoice(response.data.status === 'SECOND_FACTOR_CHOICE');
      switch (response.data.status) {
        case 'SETUP_REQUIRED':
          setStep('setup-choice');
          break;
        case 'PASSKEY_REQUIRED':
          setStep('passkey-verify');
          break;
        case 'SECOND_FACTOR_CHOICE':
          setStep('second-factor-choice');
          break;
        case 'TOTP_REQUIRED':
        default:
          setStep('totp-verify');
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen.');
    } finally {
      setIsPending(false);
    }
  }

  async function startTotpSetup() {
    setError(null);
    setIsPending(true);
    try {
      const setupResponse = await apiRequest<ItemResponse<TotpSetupResponse>>('/v1/auth/totp/setup', {
        method: 'POST',
        body: JSON.stringify({ pendingToken }),
      });
      setSetup(setupResponse.data);
      setStep('totp-setup');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Einrichtung fehlgeschlagen.');
    } finally {
      setIsPending(false);
    }
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setIsPending(true);
    try {
      const response = await apiRequest<ItemResponse<SessionResponse>>('/v1/auth/totp/verify', {
        method: 'POST',
        body: JSON.stringify({ pendingToken, code: code.trim() }),
      });
      storeToken(response.data.accessToken);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code ungültig.');
      setIsPending(false);
    }
  }

  /** Passkey-Ersteinrichtung während des Logins — schließt den Login gleich ab (ADR 0034). */
  async function runPasskeySetup() {
    setError(null);
    setIsPending(true);
    try {
      const optionsRes = await apiRequest<ItemResponse<PasskeyRegisterOptionsResponse>>(
        '/v1/auth/passkey/setup-options',
        { method: 'POST', body: JSON.stringify({ pendingToken }) },
      );
      const attestationResponse = await startRegistration({
        optionsJSON: optionsRes.data.options as PublicKeyCredentialCreationOptionsJSON,
      });
      const sessionRes = await apiRequest<ItemResponse<SessionResponse>>('/v1/auth/passkey/setup', {
        method: 'POST',
        body: JSON.stringify({
          pendingToken,
          challengeToken: optionsRes.data.challengeToken,
          attestationResponse,
          deviceLabel: guessDeviceLabel(),
        }),
      });
      storeToken(sessionRes.data.accessToken);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey-Einrichtung abgebrochen oder nicht verfügbar.');
      setIsPending(false);
    }
  }

  /** Passkey-Verifikation beim Login (Alternative zu TOTP, ADR 0034). */
  async function runPasskeyVerify() {
    setError(null);
    setIsPending(true);
    try {
      const optionsRes = await apiRequest<ItemResponse<PasskeyLoginOptionsResponse>>(
        '/v1/auth/passkey/login-options',
        { method: 'POST', body: JSON.stringify({ pendingToken }) },
      );
      const assertionResponse = await startAuthentication({
        optionsJSON: optionsRes.data.options as PublicKeyCredentialRequestOptionsJSON,
      });
      const sessionRes = await apiRequest<ItemResponse<SessionResponse>>('/v1/auth/passkey/verify', {
        method: 'POST',
        body: JSON.stringify({
          pendingToken,
          challengeToken: optionsRes.data.challengeToken,
          assertionResponse,
        }),
      });
      storeToken(sessionRes.data.accessToken);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Passkey-Anmeldung abgebrochen oder nicht verfügbar.');
      setIsPending(false);
    }
  }

  // Passkey-Schritte lösen die Geräte-Abfrage automatisch aus, sobald der
  // Schritt aktiv wird — kein Extra-Klick nötig (nur „Erneut versuchen" bei Abbruch).
  useEffect(() => {
    if (step === 'passkey-verify') void runPasskeyVerify();
    if (step === 'passkey-setup') void runPasskeySetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const meta = STEP_META[step];

  return (
    <Shell title={meta.title} subtitle={meta.subtitle} branding={branding}>
      {step === 'credentials' && endReason && (
        <p className="mb-4 rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
          {SESSION_END_TEXT[endReason]}
        </p>
      )}

      {step === 'credentials' && (
        <form onSubmit={submitCredentials} className="flex flex-col gap-4">
          <FormField label="E-Mail" htmlFor="loginEmail" required>
            <Input
              id="loginEmail"
              type="email"
              autoComplete="username"
              placeholder="name@firma.de"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoFocus
            />
          </FormField>
          <FormField label="Passwort" htmlFor="loginPassword" required>
            <Input
              id="loginPassword"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </FormField>
          <ErrorNote error={error} />
          <Button
            type="submit"
            variant="primary"
            className="mt-1 w-full justify-center"
            disabled={!email.trim() || !password}
            isLoading={isPending}
          >
            <LogIn className="h-4 w-4" strokeWidth={1.5} />
            Anmelden
          </Button>
        </form>
      )}

      {step === 'setup-choice' && (
        <div className="flex flex-col gap-3">
          <ErrorNote error={error} />
          <Button
            type="button"
            variant="primary"
            className="w-full justify-center"
            isLoading={isPending}
            onClick={startTotpSetup}
          >
            <KeyRound className="h-4 w-4" strokeWidth={1.5} />
            Mit Authenticator-App einrichten
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="w-full justify-center"
            isLoading={isPending}
            onClick={() => {
              setError(null);
              setStep('passkey-setup');
            }}
          >
            <Fingerprint className="h-4 w-4" strokeWidth={1.5} />
            Passkey einrichten
          </Button>
        </div>
      )}

      {step === 'totp-setup' && setup && (
        <form onSubmit={submitCode} className="flex flex-col gap-4">
          <p className="text-sm leading-relaxed text-muted-foreground">
            Scanne den QR-Code mit einer Authenticator-App (Google/Microsoft Authenticator, Authy …)
            und gib anschließend den 6-stelligen Code ein.
          </p>
          <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-background p-4">
            <QrCanvas value={setup.otpauthUrl} />
            <p className="break-all text-center font-mono text-xs text-muted-foreground">
              Manuell: {setup.secret}
            </p>
          </div>
          <CodeField code={code} onChange={setCode} />
          <ErrorNote error={error} />
          <Button
            type="submit"
            variant="primary"
            className="w-full justify-center"
            disabled={code.trim().length !== 6}
            isLoading={isPending}
          >
            <KeyRound className="h-4 w-4" strokeWidth={1.5} />
            2FA aktivieren & anmelden
          </Button>
        </form>
      )}

      {step === 'totp-verify' && (
        <form onSubmit={submitCode} className="flex flex-col gap-4">
          <CodeField code={code} onChange={setCode} autoFocus />
          <ErrorNote error={error} />
          <Button
            type="submit"
            variant="primary"
            className="w-full justify-center"
            disabled={code.trim().length !== 6}
            isLoading={isPending}
          >
            <KeyRound className="h-4 w-4" strokeWidth={1.5} />
            Bestätigen
          </Button>
          <BackLinks
            hasChoice={hasChoice}
            onChoice={() => {
              setStep('second-factor-choice');
              setCode('');
              setError(null);
            }}
            onCredentials={() => {
              setStep('credentials');
              setCode('');
              setError(null);
            }}
          />
        </form>
      )}

      {step === 'passkey-setup' && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-background p-8">
            {isPending ? <Spinner aria-hidden /> : <Fingerprint className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} />}
          </div>
          <ErrorNote error={error} />
          <Button
            type="button"
            variant="primary"
            className="w-full justify-center"
            isLoading={isPending}
            onClick={() => void runPasskeySetup()}
          >
            <Fingerprint className="h-4 w-4" strokeWidth={1.5} />
            Erneut versuchen
          </Button>
          <button
            type="button"
            className="mx-auto text-xs text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => {
              setStep('setup-choice');
              setError(null);
            }}
          >
            ← Stattdessen Authenticator-App verwenden
          </button>
        </div>
      )}

      {step === 'second-factor-choice' && (
        <div className="flex flex-col gap-3">
          <Button
            type="button"
            variant="primary"
            className="w-full justify-center"
            onClick={() => setStep('totp-verify')}
          >
            <KeyRound className="h-4 w-4" strokeWidth={1.5} />
            Authenticator-Code eingeben
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="w-full justify-center"
            onClick={() => setStep('passkey-verify')}
          >
            <Fingerprint className="h-4 w-4" strokeWidth={1.5} />
            Passkey verwenden
          </Button>
        </div>
      )}

      {step === 'passkey-verify' && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-background p-8">
            {isPending ? <Spinner aria-hidden /> : <Fingerprint className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} />}
          </div>
          <ErrorNote error={error} />
          <Button
            type="button"
            variant="primary"
            className="w-full justify-center"
            isLoading={isPending}
            onClick={() => void runPasskeyVerify()}
          >
            <Fingerprint className="h-4 w-4" strokeWidth={1.5} />
            Erneut versuchen
          </Button>
          <BackLinks
            hasChoice={hasChoice}
            onChoice={() => {
              setStep('second-factor-choice');
              setError(null);
            }}
            onCredentials={() => {
              setStep('credentials');
              setError(null);
            }}
          />
        </div>
      )}
    </Shell>
  );
}

/** Rückweg unter den Verifikations-Schritten — zur Faktor-Auswahl oder zur Anmeldung. */
function BackLinks({
  hasChoice,
  onChoice,
  onCredentials,
}: {
  hasChoice: boolean;
  onChoice: () => void;
  onCredentials: () => void;
}) {
  return (
    <button
      type="button"
      className="mx-auto text-xs text-muted-foreground transition-colors hover:text-foreground"
      onClick={hasChoice ? onChoice : onCredentials}
    >
      {hasChoice ? '← Anderen Faktor verwenden' : '← Zurück zur Anmeldung'}
    </button>
  );
}

/** Einheitliche Fehlermeldung (rot, mit role=alert). */
function ErrorNote({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p role="alert" className="rounded-lg border border-danger-500/30 bg-danger-50 px-3 py-2 text-sm text-danger-500">
      {error}
    </p>
  );
}

function CodeField({
  code,
  onChange,
  autoFocus,
}: {
  code: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <Label htmlFor="totpCode" className="text-xs text-muted-foreground">
        6-stelliger Code
      </Label>
      <InputOTP
        id="totpCode"
        maxLength={6}
        inputMode="numeric"
        autoComplete="one-time-code"
        value={code}
        onChange={(value) => onChange(value.replace(/\D/g, ''))}
        autoFocus={autoFocus}
      >
        <InputOTPGroup>
          <InputOTPSlot index={0} />
          <InputOTPSlot index={1} />
          <InputOTPSlot index={2} />
          <InputOTPSlot index={3} />
          <InputOTPSlot index={4} />
          <InputOTPSlot index={5} />
        </InputOTPGroup>
      </InputOTP>
    </div>
  );
}
